import { Close, ExpandMore } from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import { useMemo } from "react";

export default function MultiSelectSortFilterPopover({
  field,
  headerName,
  rows,
  options,
  filterValue,
  sortField,
  sortDirection,
  onFilterValueChange,
  onApplyFilter,
  onSortChange,
  onClose,
  showSort = true,
  applyButtonLabel = "Filter",
}) {
  const selectedValues = useMemo(
    () => (filterValue ? String(filterValue).split(",").filter(Boolean) : []),
    [filterValue]
  );

  const uniqueValues = useMemo(() => {
    if (Array.isArray(options) && options.length > 0) {
      return options.map((option) =>
        typeof option === "string" ? option : option.value
      );
    }

    const values = new Set();
    for (const row of rows || []) {
      const value = row?.[field];
      if (typeof value === "string" && value.trim()) {
        values.add(value.trim());
      }
    }
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [field, options, rows]);

  const activeSortDirection = sortField === field ? sortDirection : "";

  return (
    <Box sx={{ minWidth: showSort ? 500 : 260, width: showSort ? 600 : 320, p: 0, m: 0, pb: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          pt: 0.5,
          pb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "bold", color: "#111827" }}>
          Filter {headerName || "Column"}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ p: 0.5, color: "#6B7280" }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <Divider sx={{ width: "100%", m: 0, p: 0 }} />

      <Box sx={{ display: "flex", gap: 2, p: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ pb: 1, color: "#627CFB", fontWeight: 500, fontSize: "0.875rem" }}
          >
            {headerName || "Values"}
          </Typography>

          <FormControl fullWidth>
            <InputLabel sx={{ color: "#9CA3AF", fontSize: "0.875rem" }}>
              Select {headerName || "Values"}
            </InputLabel>
            <Select
              multiple
              value={selectedValues}
              onChange={(event) => {
                const value = event.target.value;
                const nextValue = typeof value === "string" ? value : value.join(",");
                onFilterValueChange(nextValue);
              }}
              label={`Select ${headerName || "Values"}`}
              IconComponent={ExpandMore}
              renderValue={(selected) => (
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  {selected.length === 0 ? "" : `${selected.length} selected`}
                </Typography>
              )}
              sx={{
                fontSize: "0.875rem",
                width: "100%",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#627CFB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#627CFB",
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 320,
                    zIndex: 99999,
                  },
                },
                style: {
                  zIndex: 99999,
                },
              }}
            >
              {uniqueValues.map((value) => {
                const matchingOption = Array.isArray(options)
                  ? options.find((option) =>
                      (typeof option === "string" ? option : option.value) === value
                    )
                  : null;
                const label = matchingOption && typeof matchingOption !== "string"
                  ? matchingOption.label
                  : value;

                return (
                <MenuItem
                  key={value}
                  value={value}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                    px: 1.5,
                    minHeight: "auto",
                    "&:hover": {
                      backgroundColor: "#F3F4F6",
                    },
                  }}
                >
                  <Checkbox
                    checked={selectedValues.includes(value)}
                    size="small"
                    sx={{
                      color: "#D1D5DB",
                      "&.Mui-checked": {
                        color: "#627CFB",
                      },
                      p: 0,
                    }}
                  />
                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    {label}
                  </Typography>
                </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {showSort && <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ pb: 1, color: "#627CFB", fontWeight: 500, fontSize: "0.875rem" }}
          >
            Sort by
          </Typography>

          <FormControl fullWidth>
            <InputLabel sx={{ color: "#9CA3AF", fontSize: "0.875rem" }}>
              Select Sort Option
            </InputLabel>
            <Select
              value={activeSortDirection}
              onChange={(event) => {
                const direction = event.target.value;
                onClose?.();

                setTimeout(() => {
                  onSortChange(direction ? field : null, direction || null);
                  if (filterValue) {
                    onApplyFilter(filterValue);
                  }
                }, 0);
              }}
              label="Select Sort Option"
              IconComponent={ExpandMore}
              renderValue={(selected) => (
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  {selected === "asc"
                    ? "Ascending (A-Z)"
                    : selected === "desc"
                      ? "Descending (Z-A)"
                      : ""}
                </Typography>
              )}
              sx={{
                fontSize: "0.875rem",
                width: "100%",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#627CFB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#627CFB",
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 320,
                    zIndex: 99999,
                  },
                },
                style: {
                  zIndex: 99999,
                },
              }}
            >
              <MenuItem value="asc" sx={{ py: 1, px: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  Ascending (A-Z)
                </Typography>
              </MenuItem>
              <MenuItem value="desc" sx={{ py: 1, px: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  Descending (Z-A)
                </Typography>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>}
      </Box>

      <Button
        onClick={() => {
          onApplyFilter(filterValue || "");
          onClose?.();
        }}
        variant="contained"
        fullWidth
        sx={{
          mt: 1.5,
          backgroundColor: "#627CFB",
          color: "white",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.875rem",
          "&:hover": {
            backgroundColor: "#536DEC",
          },
        }}
      >
        {applyButtonLabel}
      </Button>

      {(filterValue || sortField === field) && (
        <Button
          onClick={() => {
            onFilterValueChange("");
            onApplyFilter("");
            onClose?.();
            if (showSort) {
              setTimeout(() => {
                onSortChange(null, null);
              }, 0);
            }
          }}
          size="small"
          fullWidth
          sx={{
            mt: 1.5,
            color: "#627CFB",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            "&:hover": {
              backgroundColor: "rgba(98, 124, 251, 0.04)",
            },
          }}
        >
          {showSort ? "Clear Filter & Sort" : "Clear Filter"}
        </Button>
      )}
    </Box>
  );
}
